import { toArray } from "@suse-o11y/to-array";

export class ImmutableSet<T> implements ReadonlySetLike<T> {
	public static fromArray<T>(array: undefined | ReadonlyArray<T>): ImmutableSet<T> {
		return new ImmutableSet(new Set(array));
	}

	public static empty: ImmutableSet<never> = new ImmutableSet(new Set());

	private constructor(private readonly set: ReadonlySet<T>) {}

	protected toJSON(): unknown {
		return this.toArray();
	}

	public get size(): number {
		return this.set.size;
	}

	public has(value: T): boolean {
		return this.set.has(value);
	}

	public delete(value: T): ImmutableSet<T> {
		if (!this.has(value)) {
			return this;
		}

		const clone = new Set(this.set);

		clone.delete(value);

		return new ImmutableSet(clone);
	}

	public add(value: T): ImmutableSet<T> {
		if (this.has(value)) {
			return this;
		}

		const clone = new Set(this.set);

		return new ImmutableSet(clone.add(value));
	}

	public clear(): ImmutableSet<T> {
		if (this.size === 0) {
			return this;
		}

		return ImmutableSet.empty;
	}

	public toggle(value: T): ImmutableSet<T> {
		return this.has(value) ? this.delete(value) : this.add(value);
	}

	public intersect(other: Iterable<T>): ImmutableSet<T> {
		const set = new Set<T>();

		for (const value of other) {
			if (this.has(value)) {
				set.add(value);
			}
		}

		if (set.size === this.size) {
			return this;
		}

		return new ImmutableSet(set);
	}

	public forEach(callbackfn: (key: T, value: T, set: ReadonlySet<T>) => void, thisArg?: unknown): void {
		this.set.forEach(callbackfn, thisArg);
	}

	public entries(): SetIterator<[T, T]> {
		return this.set.entries();
	}

	public keys(): SetIterator<T> {
		return this.set.keys();
	}

	public values(): SetIterator<T> {
		return this.set.values();
	}

	public [Symbol.iterator](): SetIterator<T> {
		return this.set[Symbol.iterator]();
	}

	public toArray(): Array<T> {
		return toArray(this.set);
	}
}
